import AsyncStorage from '@react-native-async-storage/async-storage';
import { GENERAL_API_URL, API_BASE_URL, ZCLUB_API_URL } from '../config/api';

/**
 * Voucher promo items with kegunaan "coupon" use full-bleed artwork (hide badge/title/subtitle).
 */
export function isPromoVoucherCouponKegunaan(item) {
  if (item.type !== 'voucher' || !item.data) return false;
  const k = item.data.kegunaan ?? item.data.kegunaan_coupon;
  return k === 'coupon';
}

/**
 * Test API connection
 */
export const testApi = async () => {
  try {
    const response = await fetch(`${GENERAL_API_URL}/test`);
    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('API test failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get combined promo corner data (e-stamps and vouchers)
 * This function fetches both available e-stamp events and vouchers for the promo corner
 */
export const getPromoCornerData = async () => {
  try {
    const sessionId = await AsyncStorage.getItem('session_id');
    const rememberToken = await AsyncStorage.getItem('remember_token');
    
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add authentication headers if available
    if (sessionId) {
      headers['X-Session-ID'] = sessionId;
    }
    if (rememberToken) {
      headers['X-Remember-Token'] = rememberToken;
    }

    // Fetch both e-stamps and vouchers concurrently
    const [estampsResponse, vouchersResponse] = await Promise.all([
      fetch(`${GENERAL_API_URL}/zclub/zclubestamps/events`, {
        method: 'GET',
        headers,
      }),
      fetch(`${GENERAL_API_URL}/zclub/coupons`, {
        method: 'GET',
        headers,
      })
    ]);

    const estampsData = await estampsResponse.json();
    const vouchersData = await vouchersResponse.json();

    // Process e-stamps data
    const estamps = estampsData.success ? (estampsData.data || []) : [];
    
    // Process vouchers data
    const vouchers = (vouchersData.data?.coupons || []);

    // Combine and format the data for promo corner
    const promoItems = [
      ...estamps.map(estamp => ({
        id: `estamp_${estamp.id}`,
        type: 'estamp',
        title: estamp.name || estamp.brand_name || 'E-Stamp Event',
        subtitle: `Collect ${estamp.required_stamps || estamp.stamp_required || 8} stamps`,
        image: estamp.image ? `${API_BASE_URL}/uploads/estamp/${estamp.image}` : null,
        backgroundColor: '#10B981',
        textColor: '#FFFFFF',
        data: estamp,
        isEmptyCoupon: false,
        externalURL: null,
        highlightImage: null,
      })),
      ...vouchers.map(voucher => ({
        id: `voucher_${voucher.id}`,
        type: 'voucher',
        title: voucher.nama_kupon || 'Special Voucher',
        subtitle: voucher.deskripsi || `${voucher.nilai_diskon}% discount`,
        image: voucher.gambar_kupon ? `${API_BASE_URL}/uploads/kupon/${voucher.gambar_kupon}` : null,
        backgroundColor: '#3B82F6',
        textColor: '#FFFFFF',
        data: {
          ...voucher,
          kegunaan_coupon: voucher.kegunaan_coupon ?? voucher.kegunaan ?? 'coupon',
        },
        isEmptyCoupon: voucher.is_kupon_kosong,
        externalURL: voucher.link_tujuan,
        highlightImage: voucher.gambar_pop_up ? `${API_BASE_URL}/uploads/kupon/${voucher.gambar_pop_up}` : (voucher.gambar_kupon ? `${API_BASE_URL}/uploads/kupon/${voucher.gambar_kupon}` : null),
      }))
    ];

    return {
      success: true,
      data: {
        items: promoItems,
        estamps,
        vouchers,
        total_items: promoItems.length
      }
    };
  } catch (error) {
    console.error('Error fetching promo corner data:', error);
    return {
      success: false,
      error: error.message,
      data: {
        items: [],
        estamps: [],
        vouchers: [],
        total_items: 0
      }
    };
  }
};

/**
 * Get available e-stamp events only
 */
export const getAvailableEstamps = async () => {
  try {
    const sessionId = await AsyncStorage.getItem('session_id');
    const rememberToken = await AsyncStorage.getItem('remember_token');
    
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add authentication headers if available
    if (sessionId) {
      headers['X-Session-ID'] = sessionId;
    }
    if (rememberToken) {
      headers['X-Remember-Token'] = rememberToken;
    }

    const response = await fetch(`${GENERAL_API_URL}/zclub/zclubestamps/events`, {
      method: 'GET',
      headers,
    });
    const data = await response.json();
    
    if (data.success) {
      return {
        success: true,
        data: data.data || []
      };
    } else {
      return {
        success: false,
        error: data.message || 'Failed to fetch e-stamps',
        data: []
      };
    }
  } catch (error) {
    console.error('Error fetching e-stamps:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

/**
 * Get available vouchers only
 */
export const getAvailableVouchers = async (limit = 10, page = 1) => {
  try {
    console.log('getAvailableVouchers called with limit:', limit, 'page:', page);
    
    // Build query parameters
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    
    const queryString = params.toString();
    const url = `${ZCLUB_API_URL}/coupons${queryString ? `?${queryString}` : ''}`;
    
    console.log('Making voucher API call to:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    const result = await response.json();
    console.log('getAvailableVouchers API call successful');
    
    if (result.success) {
      return {
        success: true,
        data: result.data || { coupons: [], pagination: {} }
      };
    } else {
      return {
        success: false,
        error: result.message || 'Failed to fetch vouchers',
        data: { coupons: [], pagination: {} }
      };
    }
  } catch (error) {
    console.error('Error fetching vouchers:', error);
    return {
      success: false,
      error: error.message,
      data: { coupons: [], pagination: {} }
    };
  }
};